import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import i18next from '@setup/locales/i18n';
import { NotificationService } from '@setup/notifications//SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { SettingsRoutesPathEnum } from '@setup/router/routes/settings/settingsRoutesPath.enum';
import { ModalNamesEnum } from '@shared/enums/modalNames.enum';
import { StatusServerErrors } from '@shared/enums/status-server-errors';
import modalObserver from '@shared/utils/observers/modalObserver';

import { changeRedirectLink, setLoading } from '../Common/commonSlice';

import { setProfileViewDataItem } from './profile.slice';

import type { AppDispatch } from '../store';
import type { UserFullNameModalPayload } from '@compositeComponents/modals/routing/UserFullNameModal/components/UserFullNameModalContainer/UserFullNameModalContainer';
import type { FeedBackFormType } from '@compositeComponents/modals/setup/FeedbackModal/FeedbackModalContainer/FeedbackModalContainer';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { HubModel } from '@shared/models/hub.model';
import type { HubLinkingModel } from '@shared/models/hubLinking.model';
import type { ItemUserModel } from '@shared/models/itemUser.model';
import type { GeneralSettingsFormModel } from '@shared/models/profile/generalSettingsForm.model';
import type { ProfileModel } from '@shared/models/profile/profile.model';
import type { ProfileAppearanceFormModel } from '@shared/models/profile/profileAppearanceForm.model';
import type { CreateBodyArtFormModel } from '@shared/models/profile/profileBodyArtForm.model';
import type { ProfileContactsFormModel } from '@shared/models/profile/profileContactsForm.model';
import type { ProfileGeneralInfoFormModel } from '@shared/models/profile/profileGeneralInfoForm.model';
import type { ProfileSettingsModel } from '@shared/models/profile/profileSettings.model';
import type { VerificationLoginFormModel } from '@shared/models/verificationLoginFormModel';
import type { Nullable } from '@shared/types/base-custom.type';

export const getProfileInfo = createAsyncThunk<
  ProfileModel,
  undefined,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getProfileInfo', async (_, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true));
    const result = await api.profile.getProfile();
    if (result.expired.length) {
      dispatch(
        changeRedirectLink(`${SettingsRoutesPathEnum.settings}/${SettingsRoutesPathEnum.mySubscription}`),
      );
      modalObserver.addModal(ModalNamesEnum.payment, {
        props: {
          isBilling: true,
          expired: result.expired[0],
        },
      });
    }

    return result;
  } catch (e) {
    const axiosError = e as ServerErrorType;

    if (axiosError.status === StatusServerErrors.AuthorizationException) {
      return rejectWithValue(axiosError.data);
    } else {
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  } finally {
    dispatch(setLoading(false));
  }
});

export const updateProfileGeneralInfo = createAsyncThunk<
  ProfileModel,
  ProfileGeneralInfoFormModel,
  { rejectValue: ErrorType }
>('updateProfileGeneralInfo', async (props, { rejectWithValue }) => {
  try {
    return await api.profile.updateProfileGeneralInfo(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateProfileSidebarItemPosition = createAsyncThunk<any, any, { rejectValue: ErrorType }>(
  'updateProfileSidebarItemPosition',
  async (props, { rejectWithValue }) => {
    try {
      return await api.profile.updateProfileSidebarItemPosition(props);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);
export const removeProfileSidebarItem = createAsyncThunk<
  number,
  { hubsId: number },
  { rejectValue: ErrorType }
>('removeProfileSidebarItem', async (props, { rejectWithValue }) => {
  try {
    return await api.profile.removeProfileSidebarItem(props).then(() => props.hubsId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const addProfileSidebarItem = createAsyncThunk<
  { id: number; name: string; tag: string; position: number },
  { hubsId: number },
  { rejectValue: ErrorType }
>('addProfileSidebarItem', async (props, { rejectWithValue }) => {
  try {
    return await api.profile.addProfileSidebarItem(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getSidebarHubItemsForAdd = createAsyncThunk<
  { id: number; name: string; tag: string }[],
  undefined,
  { rejectValue: ErrorType }
>('getSidebarHubItemsForAdd', async (props, { rejectWithValue }) => {
  try {
    return await api.profile.getSidebarHubItemsForAdd();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateProfileFullName = createAsyncThunk<
  ProfileModel,
  UserFullNameModalPayload,
  { rejectValue: ErrorType }
>('updateProfileFullName', async (data, { rejectWithValue }) => {
  try {
    return await api.profile.updateProfileFullName(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const connectGoogleSync = createAsyncThunk<any, { code: string }, { rejectValue: ErrorType }>(
  'connectGoogleSync',
  async (data, { rejectWithValue }) => {
    try {
      return await api.profile.connectGoogleSync(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;

      if (axiosError.status === StatusServerErrors.GoogleSyncError) {
        NotificationService.error(
          axiosError?.data?.message || i18next.t('general.notifications.googleAccountAlreadyLinked'),
        );
        return rejectWithValue(axiosError.data);
      } else {
        handleServerError(e);
        return rejectWithValue(axiosError.data);
      }
    }
  },
);

export const disconnectGoogleSync = createAsyncThunk<any, { providerId: number }, { rejectValue: ErrorType }>(
  'disconnectGoogleSync',
  async (data, { rejectWithValue }) => {
    try {
      return await api.profile.disconnectGoogleSync(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const linkProviderCalendarToHub = createAsyncThunk<
  any,
  { calendarId: number; linking: HubLinkingModel | null },
  { rejectValue: ErrorType }
>('linkProviderCalendarToHub', async (data, { rejectWithValue }) => {
  try {
    return await api.profile.linkProviderCalendarToHub(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const changeProfileTimezone = createAsyncThunk<
  undefined,
  { timezone_name: string },
  { rejectValue: ErrorType }
>('changeProfileTimezone', async (props, { rejectWithValue }) => {
  try {
    return await api.profile.changeProfileTimezone(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    return rejectWithValue(axiosError.data);
  }
});

export const setViewDataItem = createAsyncThunk<
  undefined,
  { entity: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('setViewDataItem', async (params, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setProfileViewDataItem(params.entity));
    return await api.profile.setViewDataItem(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const createProfileBodyArt = createAsyncThunk<
  ProfileModel,
  CreateBodyArtFormModel,
  { rejectValue: ErrorType }
>('createProfileBodyArt', async (props, { rejectWithValue }) => {
  try {
    return await api.profile.createProfileBodyArt(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateProfileBodyArt = createAsyncThunk<
  ProfileModel,
  { id: number } & CreateBodyArtFormModel,
  { rejectValue: ErrorType }
>('updateProfileBodyArt', async (props, { rejectWithValue }) => {
  try {
    return await api.profile.updateProfileBodyArt(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const deleteProfileBodyArt = createAsyncThunk<number, { id: number }, { rejectValue: ErrorType }>(
  'deleteProfileBodyArt',
  async (props, { rejectWithValue }) => {
    try {
      return await api.profile.deleteProfileBodyArt(props).then(() => props.id);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const updateProfileContacts = createAsyncThunk<
  ProfileModel,
  ProfileContactsFormModel,
  { rejectValue: ErrorType }
>('updateProfileContacts', async (props, { rejectWithValue }) => {
  try {
    return await api.profile.updateProfileContacts(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateProfileAppearance = createAsyncThunk<
  ProfileModel,
  ProfileAppearanceFormModel,
  { rejectValue: ErrorType }
>('updateProfileAppearance', async (props, { rejectWithValue }) => {
  try {
    return await api.profile.updateProfileAppearance(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getConnections = createAsyncThunk<
  ItemUserModel[],
  { setLoading?: (value: boolean) => void } | undefined,
  { rejectValue: ErrorType }
>('getConnections', async (_, { rejectWithValue }) => {
  try {
    setLoading?.(true);
    return await api.profile.getConnections();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    setLoading?.(false);
  }
});

export const getProfileSettings = createAsyncThunk<any, undefined, { rejectValue: ErrorType }>(
  'getProfileSettings',
  async (_, { rejectWithValue }) => {
    try {
      return await api.profile.getProfileSettings();
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      if (axiosError.status === StatusServerErrors.AuthorizationException) {
        return rejectWithValue(axiosError.data);
      } else {
        handleServerError(e);
        return rejectWithValue(axiosError.data);
      }
    }
  },
);

export const setProfileSettings = createAsyncThunk<any, any, { rejectValue: ErrorType }>(
  'setProfileSettings',
  async (data, { rejectWithValue }) => {
    try {
      return await api.profile.setProfileSettings(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const deleteProfile = createAsyncThunk<
  undefined,
  { password: Nullable<string> },
  { rejectValue: ErrorType }
>('deleteProfile', async (data, { rejectWithValue }) => {
  try {
    return await api.profile.deleteProfile(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    if (axiosError.status === StatusServerErrors.AuthorizationException) {
      return rejectWithValue(axiosError.data);
    } else {
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  }
});

export const setDeviceToken = createAsyncThunk<
  undefined,
  { push_token: string; device_token: string },
  { rejectValue: ErrorType }
>('setDeviceToken', async (data, { rejectWithValue }) => {
  try {
    return await api.profile.setDeviceToken(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;

    return rejectWithValue(axiosError.data);
  }
});

export const changeLoginData = createAsyncThunk<
  ProfileSettingsModel,
  GeneralSettingsFormModel,
  { rejectValue: ErrorType }
>('changeLoginData', async (data, { rejectWithValue }) => {
  try {
    return await api.profile.changeLoginData(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const cancelVerification = createAsyncThunk<any, { login: string }, { rejectValue: ErrorType }>(
  'cancelVerification',
  async (data, { rejectWithValue }) => {
    try {
      return await api.profile.cancelVerification(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const activateLogin = createAsyncThunk<
  { login: string; isPhone: boolean },
  VerificationLoginFormModel & { isPhone: boolean },
  { rejectValue: ErrorType }
>('activateLogin', async (data, { rejectWithValue }) => {
  try {
    return await api.auth.activateUser(data).then(() => {
      return { login: data.login, isPhone: data.isPhone };
    });
  } catch (e) {
    const axiosError = e as ServerErrorType;

    if (axiosError.status === StatusServerErrors.CodeExpired) {
      NotificationService.error(i18next.t('general.notifications.expiredCode'));
    } else {
      NotificationService.error(axiosError?.data?.message || i18next.t('general.notifications.defaultError'));
    }
    return rejectWithValue({ ...axiosError.data, status: axiosError.status });
  }
});

export const resendVerificationCode = createAsyncThunk<
  undefined,
  { login: string },
  { rejectValue: ErrorType }
>('resendVerificationCode', async (data, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true));

    return await api.auth.activationResendCode(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    dispatch(setLoading(false));
  }
});

export const getSettingsProfileInfo = createAsyncThunk<
  ProfileSettingsModel,
  undefined,
  { rejectValue: ErrorType }
>('getSettingsProfileInfo', async (_, { rejectWithValue }) => {
  try {
    return await api.profile.getSettingsProfile();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getSyncedCalendarProviders = createAsyncThunk<any, undefined, { rejectValue: ErrorType }>(
  'getSyncedCalendars',
  async (_, { rejectWithValue }) => {
    try {
      return await api.profile.getSyncedCalendarProviders();
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const toggleCalendarProvider = createAsyncThunk<undefined, number, { rejectValue: ErrorType }>(
  'toggleCalendarProvider',
  async (id, { rejectWithValue }) => {
    try {
      return await api.profile.toggleCalendarProvider(id);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const getFullProfileInfo = createAsyncThunk<
  ProfileSettingsModel,
  undefined,
  { rejectValue: ErrorType }
>('getFullProfileInfo', async (_, { rejectWithValue }) => {
  try {
    return await api.profile.getFullProfile();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const activateHub = createAsyncThunk<HubModel, number, { rejectValue: ErrorType }>(
  'activateHub',
  async (hubId, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      return await api.profile.activateHub(hubId);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const deletePaymentCard = createAsyncThunk<any, number, { rejectValue: ErrorType }>(
  'deletePaymentCard',
  async (cardId, { rejectWithValue }) => {
    try {
      return await api.payment.deleteCard(cardId);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const sendFeedback = createAsyncThunk<
  undefined,
  FeedBackFormType & { documents: { id: number }[] },
  { rejectValue: ErrorType }
>('sendFeedback', async (props, { rejectWithValue }) => {
  try {
    return await api.profile.feedback(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const deactivateHub = createAsyncThunk<HubModel, number, { rejectValue: ErrorType }>(
  'deactivateHub',
  async (hubId, { dispatch, rejectWithValue }) => {
    dispatch(setLoading(true));
    try {
      return await api.profile.deactivateHub(hubId);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const updateFeedsCounter = createAsyncThunk<
  { count_news: number; count_requests: number; count_notification: number },
  undefined,
  { rejectValue: ErrorType }
>('updateFeedsCounter', async (_, { rejectWithValue }) => {
  try {
    return await api.profile.updateFeedsCounter();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data as ErrorType);
  }
});
